import React, { useState, useMemo, useEffect } from 'react';
import { Icons } from '../../utils/icons';
import { authService } from '../../services/authService';
import { OFFICE_USERS } from './utils/officeUsers';
import { expandirRecurrencia } from './utils/recurrencia';

const DIAS_SEMANA = [
  { v: 1, l: 'L' }, { v: 2, l: 'M' }, { v: 3, l: 'X' }, { v: 4, l: 'J' },
  { v: 5, l: 'V' }, { v: 6, l: 'S' }, { v: 0, l: 'D' },
];

const RECORDATORIOS = [
  { v: '', l: 'Sin recordatorio' },
  { v: 5,    l: '5 minutos antes' },
  { v: 15,   l: '15 minutos antes' },
  { v: 30,   l: '30 minutos antes' },
  { v: 60,   l: '1 hora antes' },
  { v: 1440, l: '1 día antes' },
];

export default function EventoModal({ init, creador, raw, ausencias, onClose, onSaved }) {
  const esEdicion = !!init?.maestroId;
  const [tipo, setTipo] = useState(init?.tipo || 'reunion');
  const [titulo, setTitulo] = useState(init?.titulo || '');
  const [descripcion, setDescripcion] = useState(init?.descripcion || '');
  const [ubicacion, setUbicacion] = useState(init?.ubicacion || '');
  const [tagsTexto, setTagsTexto] = useState((init?.tags || []).join(', '));
  const [fechaInicio, setFechaInicio] = useState(init?.fechaInicio || '');
  const [horaInicio, setHoraInicio] = useState(init?.horaInicio || '');
  const [fechaFin, setFechaFin] = useState(init?.fechaFin || init?.fechaInicio || '');
  const [horaFin, setHoraFin] = useState(init?.horaFin || '');
  const [todoElDia, setTodoElDia] = useState(!!init?.todoElDia);
  const [participanteEmails, setParticipanteEmails] = useState(init?.participanteEmails || []);
  const [frecuencia, setFrecuencia] = useState(init?.recurrencia?.frecuencia || 'ninguna');
  const [intervalo, setIntervalo] = useState(init?.recurrencia?.intervalo || 1);
  const [diasSemana, setDiasSemana] = useState(init?.recurrencia?.diasSemana || []);
  const [hasta, setHasta] = useState(init?.recurrencia?.hasta || '');
  const [recordatorioMinutos, setRecordatorioMinutos] = useState(init?.recordatorioMinutos ?? '');
  const [clienteId, setClienteId] = useState(init?.clienteId || '');
  const [clienteNombre, setClienteNombre] = useState(init?.clienteNombre || '');
  const [clienteTelefono, setClienteTelefono] = useState(init?.clienteTelefono || '');
  const [clienteQuery, setClienteQuery] = useState(init?.clienteNombre || '');
  const [clientesTodos, setClientesTodos] = useState(null); // null = aún no cargado
  const [cargandoClientes, setCargandoClientes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Lista de clientes desde Firebase (colección "clientes", la misma que usa
  // el módulo Clientes) — se carga una vez, en paginado, la primera vez que
  // se entra en tipo "visita", y la búsqueda se filtra en memoria (sin
  // llamadas repetidas a StelOrder por cada tecla).
  useEffect(() => {
    if (tipo !== 'visita' || clientesTodos !== null || cargandoClientes) return;
    setCargandoClientes(true);
    (async () => {
      try {
        const all = [];
        let cursor = null;
        do {
          const url = `/.netlify/functions/getClientes?soloActivos=true&limit=2000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
          const r = await fetch(url, { headers: { ...authService.authHeader() } });
          const d = await r.json();
          if (!d.success) break;
          all.push(...d.clientes);
          cursor = d.nextCursor || null;
        } while (cursor);
        setClientesTodos(all);
      } catch { setClientesTodos([]); }
      finally { setCargandoClientes(false); }
    })();
  }, [tipo, clientesTodos, cargandoClientes]);

  const clienteResultados = useMemo(() => {
    const q = clienteQuery.trim().toLowerCase();
    if (q.length < 2 || !clientesTodos) return [];
    const qNum = q.replace(/\s/g, '');
    return clientesTodos.filter(c =>
      (c.nombre       || '').toLowerCase().includes(q) ||
      (c.nombreFiscal || '').toLowerCase().includes(q) ||
      (c.email        || '').toLowerCase().includes(q) ||
      (c.telefono     || '').replace(/\s/g, '').includes(qNum) ||
      (c.movil        || '').replace(/\s/g, '').includes(qNum)
    ).slice(0, 8);
  }, [clientesTodos, clienteQuery]);

  const handleClienteQueryChange = (e) => {
    setClienteQuery(e.target.value);
    setClienteId('');
  };

  const seleccionarCliente = (c) => {
    setClienteId(String(c.id));
    setClienteNombre(c.nombre || '');
    setClienteTelefono(c.telefono || c.movil || '');
    setClienteQuery(c.nombre || '');
    if (c.direccion && !ubicacion.trim()) setUbicacion(c.direccion);
  };

  const toggleParticipante = (email) => {
    setParticipanteEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };
  const toggleDiaSemana = (v) => {
    setDiasSemana(prev => prev.includes(v) ? prev.filter(d => d !== v) : [...prev, v].sort());
  };

  // Aviso de conflicto: si alguien de los seleccionados ya tiene algo a esa
  // hora. No se muestra QUÉ es lo otro que tiene (privacidad) — solo que
  // está ocupado. Se compara contra sus propios eventos, excluyendo el que
  // se está editando. También avisa si tiene una ausencia (vacaciones/baja)
  // registrada en Fichajes que se solape con la fecha del evento.
  //
  // Las tareas no bloquean horario (no reservan una franja real, ni para
  // quien las crea ni como algo con lo que otra cosa pueda "chocar") — no
  // cuentan ni como origen ni como destino del conflicto. Y si a alguno de
  // los dos lados (el nuevo evento o el existente) le falta hora y no es
  // "todo el día", no hay franja real que comparar, así que no se avisa —
  // antes cualquier evento sin hora se trataba como si ocupara el día
  // entero, lo que disparaba avisos sin que hubiera solape de verdad.
  const conflictos = useMemo(() => {
    if (!fechaInicio || !raw || tipo === 'tarea') return {};
    const rangoInicio = fechaInicio;
    const rangoFin = fechaFin || fechaInicio;

    const maestros = raw.filter(e => !e.seriePadreId && e.id !== init?.maestroId && e.tipo !== 'tarea');
    const excepcionesPorMaestro = new Map();
    for (const e of raw.filter(e => e.seriePadreId && e.seriePadreId !== init?.maestroId && e.tipo !== 'tarea')) {
      if (!excepcionesPorMaestro.has(e.seriePadreId)) excepcionesPorMaestro.set(e.seriePadreId, []);
      excepcionesPorMaestro.get(e.seriePadreId).push(e);
    }
    const ocurrencias = [];
    for (const m of maestros) {
      const excs = excepcionesPorMaestro.get(m.id) || [];
      ocurrencias.push(...expandirRecurrencia(m, excs, rangoInicio, rangoFin));
    }

    const nuevoInicio = todoElDia ? null : (horaInicio || null);
    const nuevoFin = todoElDia ? null : (horaFin || nuevoInicio);

    const resultado = {};
    for (const email of [creador.email, ...participanteEmails]) {
      const ocupado = ocurrencias.some(oc => {
        if (oc.tipo === 'tarea') return false;
        if (oc.fechaInicio > rangoFin || (oc.fechaFin || oc.fechaInicio) < rangoInicio) return false;
        const participa = (oc.participantes || []).some(p => p.userId?.toLowerCase() === email.toLowerCase() && p.estado !== 'rechazado');
        if (!participa) return false;
        if (todoElDia || oc.todoElDia) return true;
        if (!nuevoInicio || !oc.horaInicio) return false;
        const ocInicio = oc.horaInicio, ocFin = oc.horaFin || oc.horaInicio;
        return nuevoInicio < ocFin && ocInicio < nuevoFin;
      });
      const persona = OFFICE_USERS.find(u => u.email.toLowerCase() === email.toLowerCase());
      const ausencia = persona ? (ausencias || []).find(a =>
        a.tecnicoNombre?.toLowerCase() === persona.nombre.toLowerCase() &&
        rangoInicio <= a.fechaFin && rangoFin >= a.fechaInicio
      ) : null;
      resultado[email] = { ocupado, ausencia };
    }
    return resultado;
  }, [raw, ausencias, fechaInicio, fechaFin, horaInicio, horaFin, todoElDia, participanteEmails, creador.email, init?.maestroId, tipo]);

  function buildBody(soloEstaOcurrencia) {
    const body = {
      tipo, titulo: titulo.trim(), descripcion: descripcion.trim() || null, ubicacion: ubicacion.trim() || null,
      tags: tagsTexto.split(',').map(t => t.trim()).filter(Boolean),
      creadorEmail: creador.email, creadorNombre: creador.name,
      fechaInicio, horaInicio: todoElDia ? null : (horaInicio || null),
      fechaFin: fechaFin || fechaInicio, horaFin: todoElDia ? null : (horaFin || null),
      todoElDia,
      fechaVencimiento: tipo === 'tarea' ? fechaInicio : null,
      participanteEmails,
      recordatorioMinutos: recordatorioMinutos === '' ? null : Number(recordatorioMinutos),
      clienteId: tipo === 'visita' ? (clienteId || null) : null,
      clienteNombre: tipo === 'visita' ? (clienteNombre || null) : null,
      clienteTelefono: tipo === 'visita' ? (clienteTelefono || null) : null,
    };
    if (esEdicion && soloEstaOcurrencia) {
      body.seriePadreId = init.maestroId;
      body.fechaOriginal = init.fechaOriginal;
    } else {
      if (esEdicion) body.id = init.maestroId;
      body.recurrencia = frecuencia === 'ninguna' ? null : { frecuencia, intervalo: Number(intervalo) || 1, diasSemana: frecuencia === 'semanal' ? diasSemana : null, hasta: hasta || null };
    }
    return body;
  }

  async function guardar(soloEstaOcurrencia = false) {
    if (!titulo.trim()) { setError('El título es obligatorio'); return; }
    if (!fechaInicio) { setError('La fecha es obligatoria'); return; }
    if (tipo === 'visita' && !clienteId) { setError('Selecciona el cliente al que se visita'); return; }
    setSaving(true); setError('');
    try {
      const resp = await fetch('/.netlify/functions/guardarEvento', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(buildBody(soloEstaOcurrencia)),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function borrar(soloEstaOcurrencia = false) {
    if (!window.confirm(soloEstaOcurrencia ? '¿Cancelar solo esta ocurrencia?' : '¿Borrar este evento (o toda la serie)?')) return;
    setSaving(true); setError('');
    try {
      const params = new URLSearchParams({ id: init.maestroId, requesterEmail: creador.email });
      if (soloEstaOcurrencia) params.set('fechaOriginal', init.fechaOriginal);
      const resp = await fetch(`/.netlify/functions/eliminarEvento?${params}`, { method: 'DELETE', headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-bold text-gray-900">{esEdicion ? 'Editar evento' : 'Nuevo evento'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            {[['reunion', 'Reunión'], ['tarea', 'Tarea'], ['visita', 'Visita']].map(([v, l]) => (
              <button key={v} onClick={() => setTipo(v)}
                className={`flex-1 py-2 text-sm font-bold rounded-xl border transition-colors ${tipo === v ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {l}
              </button>
            ))}
          </div>

          <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título" autoFocus className={inputCls} />
          <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Descripción (opcional)" rows={2} className={inputCls} />
          <input value={tagsTexto} onChange={e => setTagsTexto(e.target.value)} placeholder="Etiquetas separadas por comas (ej: urgente, cliente)" className={inputCls} />

          {tipo === 'visita' && (
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5">Cliente a visitar</label>
              <div className="relative">
                <input value={clienteQuery} onChange={handleClienteQueryChange} placeholder="Buscar cliente por nombre o teléfono..." className={inputCls} />
                {cargandoClientes && <p className="text-xs text-gray-400 mt-1">Cargando listado de clientes...</p>}
                {!cargandoClientes && clienteQuery.trim().length >= 2 && !clienteId && clienteResultados.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Sin resultados para "{clienteQuery.trim()}"</p>
                )}
                {clienteResultados.length > 0 && (
                  <div className="mt-1 border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                    {clienteResultados.map((c) => (
                      <button key={c.id} type="button" onClick={() => seleccionarCliente(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                        <p className="font-bold text-gray-800">{c.nombre}</p>
                        {(c.telefono || c.movil) && <p className="text-xs text-gray-500">{c.telefono || c.movil}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {clienteId && (
                <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-1.5 mt-1.5">
                  ✓ Cliente seleccionado: {clienteNombre}{clienteTelefono ? ` · ${clienteTelefono}` : ''}
                </p>
              )}
            </div>
          )}

          {(tipo === 'reunion' || tipo === 'visita') && (
            <>
              <input value={ubicacion} onChange={e => setUbicacion(e.target.value)} placeholder="Ubicación (opcional)" className={inputCls} />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={todoElDia} onChange={e => setTodoElDia(e.target.checked)} />
                Todo el día
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Fecha inicio</label>
                  <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className={inputCls} />
                </div>
                {!todoElDia && (
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Hora inicio</label>
                    <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} className={inputCls} />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Fecha fin</label>
                  <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} className={inputCls} />
                </div>
                {!todoElDia && (
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Hora fin</label>
                    <input type="time" value={horaFin} onChange={e => setHoraFin(e.target.value)} className={inputCls} />
                  </div>
                )}
              </div>
            </>
          )}

          {tipo === 'tarea' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Fecha límite</label>
                <input type="date" value={fechaInicio} onChange={e => { setFechaInicio(e.target.value); setFechaFin(e.target.value); }} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Hora (opcional)</label>
                <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">Participantes</label>
            <div className="flex flex-wrap gap-2">
              {OFFICE_USERS.filter(u => u.email !== creador.email).map(u => {
                const seleccionado = participanteEmails.includes(u.email);
                const info = conflictos[u.email];
                const enAusencia = seleccionado && info?.ausencia;
                const enConflicto = seleccionado && !enAusencia && info?.ocupado;
                return (
                  <button key={u.email} type="button" onClick={() => toggleParticipante(u.email)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
                      enAusencia ? 'bg-purple-50 text-purple-700 border-purple-300'
                      : enConflicto ? 'bg-amber-50 text-amber-700 border-amber-300'
                      : seleccionado ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                    {enAusencia ? '🌴 ' : enConflicto ? '⚠ ' : ''}{u.nombre}
                  </button>
                );
              })}
            </div>
            {participanteEmails.some(e => conflictos[e]?.ausencia) && (
              <p className="text-xs text-purple-600 mt-1.5">🌴 Alguno de los participantes está de vacaciones/baja ese día.</p>
            )}
            {participanteEmails.some(e => conflictos[e]?.ocupado && !conflictos[e]?.ausencia) && (
              <p className="text-xs text-amber-600 mt-1.5">⚠ Alguno de los participantes ya tiene algo a esa hora.</p>
            )}
            {conflictos[creador.email]?.ausencia && (
              <p className="text-xs text-purple-600 mt-1">🌴 Tú tienes una ausencia registrada ese día.</p>
            )}
            {conflictos[creador.email]?.ocupado && !conflictos[creador.email]?.ausencia && (
              <p className="text-xs text-amber-600 mt-1">⚠ Tú también tienes algo a esa hora.</p>
            )}
          </div>

          {!(esEdicion && init.esRecurrente) && (
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5">Repetir</label>
              <select value={frecuencia} onChange={e => setFrecuencia(e.target.value)} className={inputCls}>
                <option value="ninguna">No se repite</option>
                <option value="diaria">Cada día</option>
                <option value="semanal">Cada semana</option>
                <option value="mensual">Cada mes</option>
              </select>
              {frecuencia !== 'ninguna' && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Cada</span>
                    <input type="number" min={1} value={intervalo} onChange={e => setIntervalo(e.target.value)} className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm" />
                    <span>{frecuencia === 'diaria' ? 'día(s)' : frecuencia === 'semanal' ? 'semana(s)' : 'mes(es)'}</span>
                  </div>
                  {frecuencia === 'semanal' && (
                    <div className="flex gap-1">
                      {DIAS_SEMANA.map(d => (
                        <button key={d.v} type="button" onClick={() => toggleDiaSemana(d.v)}
                          className={`w-8 h-8 text-xs font-bold rounded-full border transition-colors ${diasSemana.includes(d.v) ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-500 border-gray-200'}`}>
                          {d.l}
                        </button>
                      ))}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 mb-1">Hasta (opcional)</label>
                    <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className={inputCls} />
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5">Recordatorio</label>
            <select value={recordatorioMinutos} onChange={e => setRecordatorioMinutos(e.target.value)} className={inputCls}>
              {RECORDATORIOS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            {esEdicion && !init.esRecurrente && (
              <button onClick={() => borrar(false)} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors disabled:opacity-50">
                <Icons.Trash className="w-4 h-4" />Borrar
              </button>
            )}
            {esEdicion && init.esRecurrente && (
              <>
                <button onClick={() => borrar(true)} disabled={saving}
                  className="px-3 py-2 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors disabled:opacity-50">
                  Borrar esta ocurrencia
                </button>
                <button onClick={() => borrar(false)} disabled={saving}
                  className="px-3 py-2 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors disabled:opacity-50">
                  Borrar toda la serie
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
            {esEdicion && init.esRecurrente ? (
              <>
                <button onClick={() => guardar(true)} disabled={saving}
                  className="px-3 py-2 text-xs font-bold rounded-xl bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
                  Guardar esta ocurrencia
                </button>
                <button onClick={() => guardar(false)} disabled={saving}
                  className="px-3 py-2 text-xs font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                  Guardar toda la serie
                </button>
              </>
            ) : (
              <button onClick={() => guardar(false)} disabled={saving}
                className="px-4 py-2 text-sm font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
